import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { SidenavService } from '../../app/layout/sidenav/sidenav.service';

export type Theme = 'dva-default' | 'dva-light' | 'dva-dark' | 'dva-flat';
export type ThemePosition = 'fixed' | 'above-fixed' | 'static';

export interface ThemeConfig {
  navigation: 'side' | 'top';
  sidenavUserVisible: boolean;
  toolbarVisible: boolean;
  toolbarPosition: ThemePosition;
  footerVisible: boolean;
  footerPosition: ThemePosition;
  footerType: 'initial' | 'report';
  footerReportIdentifier: string;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private _themeSubject = new BehaviorSubject<[Theme, Theme]>([null, 'dva-default']);
  theme$ = this._themeSubject.asObservable();
  activeTheme$ = this.theme$.pipe(map((theme) => theme[1]));
  private _configSubject = new BehaviorSubject<ThemeConfig>({
    navigation: 'top',
    sidenavUserVisible: true,
    toolbarVisible: true,
    toolbarPosition: 'fixed',
    footerVisible: false,
    footerPosition: 'fixed',
    footerType: 'initial',
    footerReportIdentifier: '',
  });
  config$ = this._configSubject.asObservable();

  constructor(private sidenavService: SidenavService) {
    this.restoreThemeToStored();
  }

  setTheme(theme: Theme) {
    this._themeSubject.next([this._themeSubject.getValue()[1], theme]);
    localStorage.setItem('dvatheme', theme);
  }

  setThemeDontStore(theme: Theme) {
    this._themeSubject.next([this._themeSubject.getValue()[1], theme]);
  }

  restoreThemeToStored() {
    let theme = localStorage.getItem('dvatheme');
    this.setTheme((theme as Theme) ?? 'dva-default');
  }

  setNavigation(navigation: 'side' | 'top') {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      navigation,
    });
  }

  setSidenavUserVisible(sidenavUserVisible: boolean) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      sidenavUserVisible,
    });
  }

  setToolbarVisible(toolbarVisible: boolean) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      toolbarVisible,
    });
  }

  setToolbarPosition(toolbarPosition: ThemePosition) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      toolbarPosition,
    });
  }

  setFooterVisible(footerVisible: boolean) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      footerVisible,
    });
  }

  setFooterPosition(footerPosition: ThemePosition) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      footerPosition,
    });
  }

  setFooterType(footerType: 'initial' | 'report') {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      footerType,
    });
  }

  setFooterReportIdentifier(footerReportIdentifier: string) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      footerReportIdentifier,
    });
  }

  setFooterReport(footerReportIdentifier: string) {
    this.setFooterVisible(true);
    this.setFooterType('report');
    this.setFooterReportIdentifier(footerReportIdentifier);    
  }

  setStyle(style: 'default' | 'flat' | 'dark' | 'light' | 'top' | string) {
    switch (style) {
      case 'flat': {
        this._configSubject.next({
          navigation: 'side',
          sidenavUserVisible: false,
          toolbarVisible: true,
          toolbarPosition: 'static',
          footerVisible: true,
          footerPosition: 'static',
          footerType: 'initial',
          footerReportIdentifier: '',
        });

        this.sidenavService.setCollapsed(true);
        this.setTheme('dva-flat');
        break;
      }

      case 'dark': {
        this.setTheme('dva-dark');
        break;
      }

      case 'light': {
        this._configSubject.next({
          navigation: 'side',
          sidenavUserVisible: false,
          toolbarVisible: true,
          toolbarPosition: 'static',
          footerVisible: true,
          footerPosition: 'static',
          footerType: 'initial',
          footerReportIdentifier: '',
        });

        this.setTheme('dva-light');
        break;
      }

      case 'top': {
        this._configSubject.next({
          navigation: 'top',
          sidenavUserVisible: false,
          toolbarVisible: true,
          toolbarPosition: 'fixed',
          footerVisible: true,
          footerPosition: 'fixed',
          footerType: 'initial',
          footerReportIdentifier: '',
        });
        break;
      }
    }
  }
}
